import { type ReactNode, isValidElement } from 'react';

import { ErrorCode } from './graphql';

function onlyText(message: ReactNode): string {
  if (typeof message === 'string') {
    return message;
  }
  if (Array.isArray(message)) {
    return message.map(onlyText).join('');
  }
  if (isValidElement(message)) {
    return onlyText(message.props.children);
  }
  return '';
}

export class GraphQLAppError extends Error {
  readonly code?: ErrorCode;
  readonly enrichedMessage?: ReactNode;
  readonly reason?: string;
  readonly domain?: string;

  constructor(
    message: string | ReactNode,
    code?: ErrorCode,
    errorInfo: { reason?: string | null; domain?: string | null } = {},
  ) {
    super(onlyText(message));
    this.name = 'GraphQLAppError';
    this.code = code;
    this.enrichedMessage = message;
    this.reason = errorInfo.reason ?? undefined;
    this.domain = errorInfo.domain ?? undefined;
  }
}

export const isInsufficientCreditsError = (
  error: unknown,
): error is GraphQLAppError =>
  error instanceof GraphQLAppError &&
  error.code === ErrorCode.ResourceExhausted &&
  error.reason === 'INSUFFICIENT_CREDITS' &&
  error.domain === 'confidence.spotify.com';

const INSUFFICIENT_CREDITS_MESSAGE =
  'Your workspace has run out of credits. Ask a workspace admin to add credits, then try again.';

export const getUserFacingErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string =>
  isInsufficientCreditsError(error)
    ? INSUFFICIENT_CREDITS_MESSAGE
    : fallbackMessage;

export const isNotFoundError = (error: unknown): error is GraphQLAppError =>
  error instanceof GraphQLAppError && error.code === ErrorCode.NotFound;

export const isPermissionDeniedError = (
  error: unknown,
): error is GraphQLAppError =>
  error instanceof GraphQLAppError && error.code === ErrorCode.PermissionDenied;
